import * as React from 'react';
import {StyleProp, Text, TouchableOpacity, View, ViewStyle, Platform} from 'react-native';
import {images} from '../../../assets/icons';
import {Image} from '../image';
import {AcceptorResponsePayload} from '../../models/api';
import {useTheme} from '../../theme';
import {Button} from '../button';
import {openExternalLink} from '../../pipes';
import {Link} from '../link';
import {formatPhone, formatPhoneToCallable, stripHTML} from '../../pipes';
import {useAlign} from '../../theme/helpers';
import {GetStyles} from './style';
import {Settings} from '../../constants/config';

interface Props {
  acceptor: AcceptorResponsePayload;
  displayPhone?: boolean;
  displayWebsite?: boolean;
  style?: StyleProp<ViewStyle>;
  useHoverEffect?: boolean;
  onPress?: () => void;
}

const HEADER_BLOCK_SIZE = 205;

export const AcceptorCard: React.FC<Props> = ({
  acceptor: {id, name, logo, descriptionShort, phone, webSiteURL},
  style,
  displayPhone = false,
  displayWebsite = false,
  useHoverEffect,
  onPress,
}) => {
  const theme = useTheme();
  const align = useAlign();
  const headerBlockSize = align(HEADER_BLOCK_SIZE);

  const styles = GetStyles(theme, headerBlockSize, align);

  const settings = Settings.getSettings();

  const logoImageUrl = React.useMemo(() => {
    return logo?.startsWith('http') ? logo : Platform.OS !== 'web' ? `${settings.webProxyUrl}${logo}` : logo;
  }, [logo]);

  return (
    <Container
      testID={`acceptor-item-${id}-id`}
      onPress={onPress}
      style={[styles.container, style]}
      useHoverEffect={useHoverEffect}>
      <React.Fragment>
        <View testID={`acceptor-item-${id}-header-id`} style={styles.header}>
          <CardHeaderShape color={theme.color.grayMenu} size={headerBlockSize} />
          <View testID={`acceptor-item-${id}-logo-id`} style={styles.headerImageBlock}>
            <Image style={styles.logo} source={{uri: logoImageUrl}} resizeMode='contain' />
          </View>
        </View>
        <View testID={`acceptor-item-${id}-body-id`} style={styles.body}>
          <Text testID={`acceptor-item-${id}-name-id`} style={styles.name} numberOfLines={2}>
            {name}
          </Text>
          <View testID={`acceptor-item-${id}-descriptionContainer-id`} style={styles.descriptionContainer}>
            <Text testID={`acceptor-item-${id}-description-id`} style={styles.description} numberOfLines={3}>
              {stripHTML(descriptionShort)}
            </Text>
          </View>
          {displayPhone && (
            <View testID={`acceptor-item-${id}-phone-id`} style={styles.phoneContainer}>
              <Link
                testID='acceptorcard-phone-link-id'
                href={formatPhoneToCallable(phone)}
                external
                style={styles.phone}>
                <View style={styles.phoneIcon}>
                  <images.Phone width={18} height={18} color={styles.phoneIcon.color} />
                </View>
                <Text style={styles.phoneText}>{formatPhone(phone)}</Text>
              </Link>
            </View>
          )}
        </View>
        {displayWebsite && (
          <View testID={`acceptor-item-${id}-web-id`} style={styles.footer}>
            <Button
              id={`acceptor-${id}-web-site`}
              style={styles.button}
              onPress={() => openExternalLink(webSiteURL)}
              text='Веб-сайт'
              block
              type='secondary'
              disabled={!webSiteURL}
            />
          </View>
        )}
      </React.Fragment>
    </Container>
  );
};

const CardHeaderShape: React.FC<{color: string; size: number}> = (props) => {
  if (Platform.OS === 'web') {
    const style: ViewStyle = {
      width: '100%',
      height: '100%',
      position: 'absolute',
      left: 0,
      bottom: 0,
      zIndex: 0,
    };
    return <images.LoyaltyAcceptorCardTracery style={style} color={props.color} />;
  }
  const scale = 2;
  const style: ViewStyle = {
    width: props.size,
    height: props.size,
    backgroundColor: props.color,
    borderRadius: props.size,
    top: -(props.size * scale - props.size) * 0.5,
    transform: [{scale}],
  };
  return <View style={style} />;
};

interface ContainerProps {
  testID: string;
  children: React.ReactFragment;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  useHoverEffect?: boolean;
}

const Container = (props: ContainerProps) => {
  const {children, onPress, useHoverEffect, style, testID} = props;

  if (onPress) {
    return (
      <TouchableOpacity
        testID={testID}
        activeOpacity={Platform.OS === 'web' ? 1 : 0.5}
        onPress={onPress}
        style={style}
        {...{dataSet: {class: useHoverEffect ? 'acceptor-card' : 'acceptor-card--noHover'}}}>
        {children}
      </TouchableOpacity>
    );
  }

  return <View style={style}>{children}</View>;
};
